import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/core/context'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

/* ── The objects a report can be about ───────────────────────────────── */

/**
 * What a report is run for. Each target says which report types can cover it,
 * which parameter names it, and what the schedule endpoint calls its type when
 * asked for everything scheduled against it.
 *
 * Only line items are wired up so far; the other entries are what the same card
 * needs to sit on an order or an advertiser page.
 */
export const REPORT_TARGETS = {
  lineItem: { parameter: 'lineItemId', relatedType: 'LineItem', reportTypes: ['LINE_ITEM'] },
  order: { parameter: 'orderId', relatedType: 'Order', reportTypes: ['LINE_ITEM_LIST'] },
  advertiser: { parameter: 'advertiserId', relatedType: 'Advertiser', reportTypes: ['ADVERTISER'] },
  adUnit: { parameter: 'adUnitId', relatedType: 'AdUnit', reportTypes: ['AD_UNIT'] },
  site: { parameter: 'siteId', relatedType: 'Site', reportTypes: ['SITE'] },
} as const

export type ReportTargetType = keyof typeof REPORT_TARGETS

/** The object a card reports on — its id, and what to call it on screen. */
export interface ReportTarget {
  type: ReportTargetType
  id: string
  name?: string
  /** The object's own dates, which the flight-based periods report over. */
  startDate?: string
  endDate?: string
}

/** Query parameter naming the target on the executions endpoint. */
const EXECUTION_QUERY_PARAM: Record<ReportTargetType, string> = {
  lineItem: 'lineItem',
  order: 'order',
  advertiser: 'advertiser',
  adUnit: 'adUnit',
  site: 'site',
}

/* ── Templates ───────────────────────────────────────────────────────── */

export interface ReportTemplate {
  id: string
  name: string
  description: string
  reportType: string
  reportFormat: string
}

/* ── Executions — one report that was or is being produced ───────────── */

export type ReportStatus =
  | 'PENDING'
  | 'QUEUED'
  | 'PROCESSING'
  | 'REJECTED'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'TIMED_OUT'

/** A report the engine has not finished with yet. */
export function isUnfinished(status: ReportStatus): boolean {
  return status === 'PENDING' || status === 'QUEUED' || status === 'PROCESSING'
}

export interface ReportExecution {
  id: string
  name: string
  status: ReportStatus
  /** The kind of object the report covers, e.g. `LINE_ITEM`. */
  reportType: string
  createTime: string
  executionStartTime: string
  executionEndTime: string
  /** CDN link to the report itself, and to the second format it is also built in. */
  result: string
  secondResult: string
  templateId: string
  templateName: string
  /** Set when a schedule produced this report rather than a person. */
  scheduleId: string
  /** What the report covers, as the report engine was given it. */
  parameters: Record<string, unknown>
  messages: string[]
}

/* ── Schedules — the standing order that produces executions ─────────── */

/** How often a schedule runs. */
export type Recurrence = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'END_OF_FLIGHT' | 'ONCE'

/**
 * What each run covers. `RECURRENCE_PERIOD` means "the interval that just
 * ended" — the previous day for a daily schedule, the previous week for a
 * weekly one — while `FLIGHT` means the whole campaign to date.
 */
export type ReportPeriod =
  | 'RECURRENCE_PERIOD'
  | 'FLIGHT'
  | 'DAILY'
  | 'WEEKLY'
  | 'MONTHLY'
  | 'CURRENT_MONTH'

export type WeekDay =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY'

export interface ReportRecipient {
  id: string
  name: string
}

export interface ReportSchedule {
  id: string
  name: string
  objectState: string
  recurrence: Recurrence | null
  period: ReportPeriod | null
  /** Time of day a run starts, as `HH:mm`. Midnight when unset. */
  periodTime: string
  periodDayOfWeek: WeekDay | null
  /** Day of the month a monthly schedule runs, 1-28. */
  periodDayOfMonth: number | null
  timeZone: string
  currency: string
  locale: string
  templateId: string
  templateName: string
  /** Line item template an order or advertiser report runs for each line item. */
  childTemplateId: string
  childTemplateName: string
  parameters: Record<string, unknown>
  /** The object the schedule reports on, as the API names it; null for network-wide or all-advertiser reports. */
  target: ReportRecipient | null
  recipientUsers: ReportRecipient[]
  recipientEmailAddresses: string[]
  /**
   * When the next run is due. Empty until the scheduler has picked the
   * schedule up, and far in the future for one that will not run again.
   */
  scheduledFor: string
  /** Static link that always resolves to this schedule's latest report. */
  reportUrl: string
  /** Why the schedule is not running — the API's own warnings, as text. */
  warnings: string[]
  validationWarnings: ValidationWarning[]
  createTime: string
  updateTime: string
}

export interface ReportScheduleInput {
  name: string
  reportTemplate: { id: string }
  /** `null` drops the child template. */
  childReportTemplate?: { id: string } | null
  recurrence: Recurrence
  period: ReportPeriod
  /** `null` goes back to the recurrence's default (midnight, Monday, the 1st). */
  periodTime?: string | null
  periodDayOfWeek?: WeekDay | null
  periodDayOfMonth?: number | null
  timeZone?: string
  currency?: string
  locale?: string
  parameters: Record<string, unknown>
  recipientUsers?: { id: string }[]
  recipientEmailAddresses?: string[]
  objectState?: 'ACTIVE' | 'INACTIVE' | 'HIDDEN'
}

/* ── Mapping ─────────────────────────────────────────────────────────── */

function str(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function ref(value: unknown): { id: string; name: string } {
  const r = (value ?? {}) as Record<string, unknown>
  return { id: str(r.id), name: str(r.name) }
}

/** Validation messages arrive as objects; only their text is worth showing. */
function messageTexts(value: unknown): string[] {
  return toArray<Record<string, unknown>>(value ?? [])
    .map((m) => str(m.text) || str(m.message) || str(m.code))
    .filter(Boolean)
}

function mapTemplate(r: Record<string, unknown>): ReportTemplate {
  return {
    id: str(r.id),
    name: str(r.name),
    description: str(r.description),
    reportType: str(r.reportType),
    reportFormat: str(r.reportFormat),
  }
}

function mapExecution(r: Record<string, unknown>): ReportExecution {
  const template = ref(r.reportTemplate)
  return {
    id: str(r.id),
    name: str(r.name),
    status: (str(r.status) || 'PROCESSING') as ReportStatus,
    reportType: str(r.reportType),
    createTime: str(r.createTime),
    executionStartTime: str(r.executionStartTime),
    executionEndTime: str(r.executionEndTime),
    result: str(r.result),
    secondResult: str(r.secondResult),
    templateId: template.id,
    templateName: template.name,
    scheduleId: ref(r.reportSchedule).id,
    parameters: (r.parameters as Record<string, unknown>) ?? {},
    messages: messageTexts(r.messages),
  }
}

/** `07:00:00` comes back from the API; the form only ever deals in `07:00`. */
function mapTime(value: unknown): string {
  return str(value).slice(0, 5)
}

/** Where a schedule's API output names the object it reports on. */
const SCHEDULE_TARGET_KEYS = [
  'lineItem',
  'order',
  'advertiser',
  'adUnit',
  'site',
  'siteGroup',
  'earningsAccount',
  'salespersonUser',
] as const

function mapSchedule(r: Record<string, unknown>): ReportSchedule {
  const template = ref(r.reportTemplate)
  const childTemplate = ref(r.childReportTemplate)
  return {
    id: str(r.id),
    name: str(r.name),
    objectState: str(r.objectState),
    recurrence: (str(r.recurrence) || null) as Recurrence | null,
    period: (str(r.period) || null) as ReportPeriod | null,
    periodTime: mapTime(r.periodTime),
    periodDayOfWeek: (str(r.periodDayOfWeek) || null) as WeekDay | null,
    periodDayOfMonth: typeof r.periodDayOfMonth === 'number' ? r.periodDayOfMonth : null,
    timeZone: str(r.timeZone),
    currency: str(r.currency),
    locale: str(r.locale),
    templateId: template.id,
    templateName: template.name,
    childTemplateId: childTemplate.id,
    childTemplateName: childTemplate.name,
    parameters: (r.parameters as Record<string, unknown>) ?? {},
    target:
      SCHEDULE_TARGET_KEYS.map((key) => ref(r[key])).find((target) => !!target.id) ?? null,
    recipientUsers: toArray<Record<string, unknown>>(r.recipientUsers ?? []).map((u) => ref(u)),
    recipientEmailAddresses: (r.recipientEmailAddresses as string[]) ?? [],
    scheduledFor: str(r.scheduledFor),
    reportUrl: str(r.reportUrl),
    warnings: messageTexts(r.validationWarnings),
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    createTime: str(r.createTime),
    updateTime: str(r.updateTime),
  }
}

/**
 * A schedule so far in the future it is not going to run — the API's way of
 * parking a one-off that has fired, or a schedule whose flight has ended.
 */
export function isParked(schedule: ReportSchedule): boolean {
  return !!schedule.scheduledFor && new Date(schedule.scheduledFor).getFullYear() >= 3000
}

/** The API hands a schedule's latest-report link back protocol-relative. */
export function absoluteReportUrl(url: string): string {
  return url.startsWith('//') ? `https:${url}` : url
}

/* ── Calls ───────────────────────────────────────────────────────────── */

const PAGE_SIZE = 100

export const ReportsApi = {
  /** Templates this network can run, narrowed to the ones a target supports. */
  getTemplates: async (target?: ReportTargetType): Promise<ReportTemplate[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      page: '1',
      pageSize: String(PAGE_SIZE),
      orderBy: 'name',
    })
    const res = await apiFetch(`${API_BASE}/reporttemplates?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch report templates')
    const templates = toArray(await res.json()).map(mapTemplate)
    if (!target) return templates
    const allowed = REPORT_TARGETS[target].reportTypes as readonly string[]
    return templates.filter((template) => allowed.includes(template.reportType))
  },

  /** Reports produced for one object, newest first. */
  getExecutions: async (target: ReportTarget, limit = 25): Promise<ReportExecution[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      [EXECUTION_QUERY_PARAM[target.type]]: target.id,
      orderBy: 'createTime',
      orderByDirection: 'DESCENDING',
      page: '1',
      pageSize: String(limit),
    })
    const res = await apiFetch(`${API_BASE}/reports?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch reports')
    return toArray(await res.json()).map(mapExecution)
  },

  /**
   * Runs a report now. The id is client-generated, as everywhere else in the
   * app, and the report is built in the background — it comes back
   * `PROCESSING` and the list polls it until it is done.
   */
  run: async (input: {
    name: string
    templateId: string
    /** Line item template an order or advertiser report runs for each line item. */
    childTemplateId?: string
    parameters: Record<string, unknown>
    /** Ties a line item report to its line item, so it is listed there. */
    relatedLineItemId?: string
    recipientEmailAddresses?: string[]
  }): Promise<ReportExecution> => {
    const res = await apiFetch(`${API_BASE}/reports/${newReportId()}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        name: input.name,
        reportTemplate: { id: input.templateId },
        ...(input.childTemplateId ? { childReportTemplate: { id: input.childTemplateId } } : {}),
        ...(input.relatedLineItemId ? { relatedLineItems: [{ id: input.relatedLineItemId }] } : {}),
        parameters: input.parameters,
        ...(input.recipientEmailAddresses?.length
          ? { recipientEmailAddresses: input.recipientEmailAddresses }
          : {}),
      }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to run report')
    return mapExecution(await res.json())
  },

  /** Every report produced in the network, newest first. */
  getExecutionPage: async ({
    page = 1,
    pageSize = 25,
    search,
    sort,
  }: { page?: number; pageSize?: number; search?: string; sort?: ListSort | null } = {}): Promise<
    ListPage<ReportExecution>
  > => {
    const params = new URLSearchParams({
      ...searchParams('reports', search),
      context: getContext(),
      ...sortParams(sort, { orderBy: 'createTime', orderByDirection: 'DESCENDING' }),
      page: String(page),
      pageSize: String(pageSize),
    })
    const res = await apiFetch(`${API_BASE}/reports?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch reports')
    const data = (await res.json()) as { totalCount?: number }
    const results = toArray<Record<string, unknown>>(data).map(mapExecution)
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  /** Every schedule in the network. */
  getSchedulePage: async ({
    page = 1,
    pageSize = 25,
    search,
    status,
    sort,
  }: {
    page?: number
    pageSize?: number
    search?: string
    status?: ObjectStatus
    sort?: ListSort | null
  } = {}): Promise<ListPage<ReportSchedule>
  > => {
    const params = new URLSearchParams({
      ...searchParams('reportSchedules', search),
      context: getContext(),
      includeHidden: 'false',
      ...statusParams(status),
      ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
      page: String(page),
      pageSize: String(pageSize),
    })
    const res = await apiFetch(`${API_BASE}/reportschedules?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch report schedules')
    const data = (await res.json()) as { totalCount?: number }
    const results = toArray<Record<string, unknown>>(data).map(mapSchedule)
    return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
  },

  getSchedule: async (id: string): Promise<ReportSchedule> => {
    const res = await apiFetch(`${API_BASE}/reportschedules/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch report schedule')
    return mapSchedule(await res.json())
  },

  /** Everything scheduled against one object, whether or not it is running. */
  getSchedules: async (target: ReportTarget): Promise<ReportSchedule[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      relatedId: target.id,
      relatedObjectType: REPORT_TARGETS[target.type].relatedType,
      includeActive: 'true',
      includeInactive: 'true',
      page: '1',
      pageSize: String(PAGE_SIZE),
    })
    const res = await apiFetch(`${API_BASE}/reportschedules?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch report schedules')
    return toArray(await res.json()).map(mapSchedule)
  },

  /** Creates on an unknown id, updates on a known one. */
  saveSchedule: async (id: string, input: Partial<ReportScheduleInput>): Promise<ReportSchedule> => {
    const res = await apiFetch(`${API_BASE}/reportschedules/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(input),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save report schedule')
    return mapSchedule(await res.json())
  },

  /** A new schedule with another's settings, recipients and object under a new name. */
  copySchedule: (source: ReportSchedule, newId: string, name: string): Promise<ReportSchedule> =>
    ReportsApi.saveSchedule(newId, {
      name,
      reportTemplate: { id: source.templateId },
      ...(source.childTemplateId ? { childReportTemplate: { id: source.childTemplateId } } : {}),
      recurrence: source.recurrence ?? 'WEEKLY',
      period: source.period ?? 'RECURRENCE_PERIOD',
      ...(source.periodTime ? { periodTime: source.periodTime } : {}),
      ...(source.periodDayOfWeek ? { periodDayOfWeek: source.periodDayOfWeek } : {}),
      ...(source.periodDayOfMonth ? { periodDayOfMonth: source.periodDayOfMonth } : {}),
      timeZone: source.timeZone || undefined,
      currency: source.currency || undefined,
      locale: source.locale || undefined,
      parameters: source.parameters,
      recipientUsers: source.recipientUsers.map((u) => ({ id: u.id })),
      recipientEmailAddresses: source.recipientEmailAddresses,
      objectState: 'ACTIVE',
    }),

  /** There is no DELETE: a schedule is retired by hiding it. */
  remove: async (id: string): Promise<ReportSchedule> =>
    ReportsApi.saveSchedule(id, { objectState: 'HIDDEN' }),
}

/** Ids are client-generated, matching how the rest of the app creates objects. */
export function newReportId(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 16)
}
